import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import {
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  ChartConfiguration,
  DoughnutController,
  Filler,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js';

Chart.register(
  DoughnutController,
  ArcElement,
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Filler,
  Tooltip,
  Legend,
);

/**
 * Thin, SSR-safe wrapper around a single Chart.js canvas. It (re)builds the chart
 * whenever `config` changes and keeps it sized to its container via ResizeObserver.
 */
@Component({
  selector: 'app-chart-reusable',
  standalone: true,
  host: { class: 'block h-full w-full' },
  template: `<div class="relative h-full w-full"><canvas #canvas></canvas></div>`,
})
export class ChartReusableComponent implements AfterViewInit, OnChanges, OnDestroy {
  // Accepts any concrete chart type (doughnut/bar/line) without generic-variance friction.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input({ required: true }) config!: ChartConfiguration<any>;

  @ViewChild('canvas') private canvasRef?: ElementRef<HTMLCanvasElement>;

  private chart: Chart | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private frameId: number | null = null;
  private viewReady = false;

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.observeContainer();
    this.scheduleRender();
  }

  ngOnChanges(): void {
    if (this.viewReady) {
      this.scheduleRender();
    }
  }

  ngOnDestroy(): void {
    if (this.frameId !== null && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this.frameId);
    }
    this.resizeObserver?.disconnect();
    this.chart?.destroy();
  }

  /** Defer the draw to the next frame so the container has a settled size first. */
  private scheduleRender(): void {
    if (typeof window === 'undefined' || typeof requestAnimationFrame === 'undefined') {
      this.render();
      return;
    }
    if (this.frameId !== null) cancelAnimationFrame(this.frameId);
    this.frameId = requestAnimationFrame(() => {
      this.frameId = null;
      this.render();
    });
  }

  private render(): void {
    if (typeof window === 'undefined') return;
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas || !this.config) return;

    const host = canvas.parentElement;
    // A zero-sized container renders a blank canvas that never redraws; wait for
    // the ResizeObserver to fire once the layout settles instead.
    if (host && (host.clientWidth === 0 || host.clientHeight === 0)) return;

    this.chart?.destroy();
    const merged = {
      ...this.config,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        ...(this.config.options ?? {}),
      },
    };
    // Chart.js's per-type config union doesn't narrow through a spread; the shape is
    // correct at each call site, so cast to the base config type for construction.
    this.chart = new Chart(canvas, merged as unknown as ChartConfiguration);
  }

  private observeContainer(): void {
    if (typeof window === 'undefined' || typeof ResizeObserver === 'undefined') return;
    const host = this.canvasRef?.nativeElement?.parentElement;
    if (!host) return;
    this.resizeObserver = new ResizeObserver(() => {
      // Draw now if an earlier attempt bailed on a zero-sized container;
      // otherwise just keep the existing chart sized to its box.
      if (this.chart) {
        this.chart.resize();
      } else {
        this.render();
      }
    });
    this.resizeObserver.observe(host);
  }
}
